<template>
<AppLayout>
  <div class="transaction">
    <section id="transaction-products">
      <div class="container">
        <h6 style="margin-bottom: 0px;" class="fw-bold">Purchase Requests</h6>
        
        <div class="warehouse-inventory-card-wrapper">
          <div class="warehouse-inventory-card">
            <p>Pending <br>Requests</p>
            <strong class="orange">{{ stats.pending || 0 }}</strong>
          </div>
          
          <div class="warehouse-inventory-card">
            <p>Approved <br>Requests</p>
            <strong class="green">{{ stats.approved || 0 }}</strong>
          </div>
          
          <div class="warehouse-inventory-card">
            <p>Processing <br>Requests</p>
            <strong class="blue">{{ stats.processing || 0 }}</strong>
          </div>
          
          <div class="warehouse-inventory-card">
            <p>Completed <br>Requests</p>
            <strong>{{ stats.completed || 0 }}</strong>
          </div>
          
          <div class="warehouse-inventory-card">
            <p>Rejected <br>Requests</p>
            <strong class="red">{{ stats.rejected || 0 }}</strong>
          </div>
        </div>

        <div class="filter-search" style="margin-top: 0px;padding-left: 0px;">
          <div class="filter-search-header">
            <p>Filter</p>
          </div>
          
          <div class="filter-search-content">
            <div class="form">
              <div class="row">
                <div class="col-md-2">
                  <div class="form-group">
                    <label for="status">Status</label>
                    <select v-model="filters.status" @change="loadPurchaseRequests" class="form-control">
                      <option value="">All Statuses</option>
                      <option value="PENDING">Pending</option>
                      <option value="APPROVED">Approved</option>
                      <option value="PROCESSING">Processing</option>
                      <option value="COMPLETED">Completed</option>
                      <option value="REJECTED">Rejected</option>
                    </select>
                  </div>
                </div>
                
                <div class="col-md-2">
                  <div class="form-group">
                    <label for="department">Department</label>
                    <select v-model="filters.department" @change="loadPurchaseRequests" class="form-control">
                      <option value="">All Departments</option>
                      <option v-for="d in departments" :key="d.id" :value="d.name">{{ d.name }}</option>
                    </select>
                  </div>
                </div>

                <div class="col-md-2">
                  <div class="form-group">
                    <label for="project">Project</label>
                    <select v-model="filters.project" @change="loadPurchaseRequests" class="form-control">
                      <option value="">All Projects</option>
                      <option v-for="p in projects" :key="p.id" :value="p.name">{{ p.name }}</option>
                    </select>
                  </div>
                </div>

                <div class="col-md-2">
                  <div class="form-group">
                    <label for="category">Category</label>
                    <select v-model="filters.categoryId" @change="loadPurchaseRequests" class="form-control">
                      <option value="">All Categories</option>
                      <option v-for="c in categories" :key="c.id" :value="c.id">{{ c.name }}</option>
                    </select>
                  </div>
                </div>

                <div v-if="hasPurchaseAdd" class="col-md-2">
                  <div class="form-group">
                    <label for="requester">Requester</label>
                    <input type="text" v-model="filters.requester" @input="loadPurchaseRequests" 
                           placeholder="Search by requester..." class="form-control">
                  </div>
                </div>
                
                <div class="col-md-2">
                  <div class="form-group">
                    <label for="dateFrom">Date From</label>
                    <input type="date" v-model="filters.dateFrom" @change="loadPurchaseRequests" class="form-control">
                  </div>
                </div>
                
                <div class="col-md-2">
                  <div class="form-group">
                    <label for="dateTo">Date To</label>
                    <input type="date" v-model="filters.dateTo" @change="loadPurchaseRequests" class="form-control">
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="data-table-wrapper">
          <SkeletonLoader v-if="loading" type="table" :rows="6" :columns="12" />
          <table v-else class="table table-hover table-equal-columns">
            <thead>
              <tr>
                <th>No.</th>
                <th>Request No.</th>
                <th>Requester</th>
                <th>Department</th>
                <th>Project</th>
                <th>Vehicle</th>
                <th>Category</th>
                <th>Items</th>
                <th>Status</th>
                <th>Priority</th>
                <th>Processed By</th>
                <th>Created</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="purchaseRequests.length === 0">
                <td colspan="13" class="text-center p-4">No purchase requests found</td>
              </tr>
              <tr v-for="(request, index) in purchaseRequests" :key="request.id">
                <td>{{ index + 1 }}</td>
                <td>{{ request.purchaseRequestNo }}</td>
                <td>{{ request.requestedBy?.name }}</td>
                <td>{{ request.department?.name || request.department || '-' }}</td>
                <td>{{ request.project?.name || request.project || '-' }}</td>
                <td>{{ request.vehicle?.plateNumber || '-' }}</td>
                <td>{{ formatCategory(request) }}</td>
                <td>{{ request.items?.length || 0 }}</td>
                <td>
                  <button :class="getStatusClass(request.status)" style="text-transform: capitalize !important; font-size: 11px !important; border-radius: 15px;">
                    {{ formatStatusLabel(request.status) }}
                  </button>
                </td>
                <td>
                  <span :class="getPriorityClass(request.urgency || request.priority)" class="badge rounded-pill">
                    {{ formatStatusLabel(request.urgency || request.priority) }}
                  </span>
                </td>
                <td>{{ request.processedBy?.name || '-' }}</td>
                <td>{{ formatDate(request.createdAt, true) }}</td>
                <td>
                  <div class="btn-group">
                    <router-link :to="`/transaction/purchase-request/view/${request.id}`" 
                                 class="btn btn-sm btn-info" title="View">
                      <i class="fas fa-eye"></i>
                    </router-link>
                    
                    <router-link v-if="request.status === 'APPROVED'" 
App                                 :to="`/transaction/purchase-request/view/${request.id}`" 
                                 class="btn btn-sm btn-warning" title="Process">
                      <i class="fas fa-cogs"></i>
                    </router-link>
                    
                    <button v-if="canApprove() && request.status === 'PENDING'" 
                            @click="approveRequest(request.id)"
                            class="btn btn-sm btn-success" title="Approve">
                      <i class="fas fa-check"></i>
                    </button>
                    
                    <button v-if="canReject() && request.status === 'PENDING'" 
                            @click="openRejectModal(request)"
                            class="btn btn-sm btn-danger" title="Reject">
                      <i class="fas fa-times"></i>
                    </button>
                    
                    <router-link
                      v-if="canEdit(request)"
                      :to="`/transaction/purchase-request/edit/${request.id}`"
                      class="btn btn-sm btn-primary"
                      title="Edit"
                    >
                      <i class="fas fa-edit"></i>
                    </router-link>

                    <button v-if="request.attachments?.length" 
                            @click.stop="openAttachmentsModal(request)"
                            class="btn btn-sm btn-outline-secondary" title="Attachments">
                      <i class="fas fa-paperclip"></i>
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

      </div>
    </section>

    <!-- Reject Modal -->
    <div v-if="showRejectModal" class="modal-backdrop">
      <div class="modal-dialog">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">Reject Purchase Request</h5>
            <button @click="closeRejectModal" class="btn-close"></button>
          </div>
          <div class="modal-body">
            <div class="form-group">
              <label for="rejectReason">Reason for Rejection</label>
              <textarea v-model="rejectForm.reason" 
                        id="rejectReason" 
                        class="form-control" 
                        rows="4" 
                        placeholder="Please provide a reason for rejecting this request..."></textarea>
            </div>
          </div>
          <div class="modal-footer">
            <button @click="closeRejectModal" class="btn btn-secondary">Cancel</button>
            <button @click="confirmRejectRequest" class="btn btn-danger" :disabled="!rejectForm.reason.trim()">
              Reject Request
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Attachments Modal -->
    <div v-if="showAttachmentsModal" class="modal-backdrop" @click.self="closeAttachmentsModal">
      <div class="modal-dialog" style="max-width: 600px;">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">Attachments — {{ attachmentsRequest?.purchaseRequestNo }}</h5>
            <button @click="closeAttachmentsModal" class="btn-close"></button>
          </div>
          <div class="modal-body p-4">
            <h6 class="fw-bold mb-3">Attachments <span class="text-muted fw-normal">({{ attachmentsRequest?.attachments?.length || 0 }})</span></h6>
            
            <div v-if="attachmentsRequest?.attachments?.length > 0" class="d-flex flex-column gap-3">
              <div 
                v-for="att in attachmentsRequest.attachments" 
                :key="att.id"
                class="p-3 border rounded shadow-sm d-flex align-items-center justify-content-between"
                style="background-color: #f8f9fa;"
              >
                <div class="text-primary text-truncate pe-3" style="max-width: 70%;">
                  {{ getFileName(att) }}
                </div>
                <div class="d-flex gap-2 flex-shrink-0">
                  <button class="btn btn-primary btn-sm px-3" @click="openAttachmentPreview(att)">
                    Preview
                  </button>
                  <button class="btn btn-outline-primary btn-sm px-3" @click="downloadAttachment(att)">
                    Download
                  </button>
                </div>
              </div>
            </div>
            <div v-else class="text-center py-4 text-muted">
              No attachments found for this request.
            </div>
          </div>
          <div class="modal-footer">
            <button @click="closeAttachmentsModal" class="btn btn-secondary">Close</button>
          </div>
        </div>
      </div>
    </div>

    <!-- Inline Preview Overlay -->
    <div v-if="previewAttachment" class="preview-overlay" @click.self="closePreview">
      <div class="preview-box">
        <div class="preview-header">
          <span class="preview-title">{{ getFileName(previewAttachment) }}</span>
          <button type="button" class="btn-close-preview" @click="closePreview" aria-label="Close">&times;</button>
        </div>
        <div class="preview-body">
          <img v-if="isPreviewableImage(previewAttachment)" :src="getAttachmentUrl(previewAttachment)" class="preview-content" alt="Preview" />
          <iframe v-else-if="isPreviewablePdf(previewAttachment)" :src="getAttachmentUrl(previewAttachment)" class="preview-content" title="PDF Preview"></iframe>
          <div v-else class="preview-fallback">
            <p>Preview not available for this file type.</p>
            <button class="btn btn-primary" @click="downloadAttachment(previewAttachment)">
              <i class="fas fa-download me-1"></i> Download to View
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</AppLayout>
</template>

<script>
import $ from 'jquery'
import AppLayout from '@/components/AppLayout.vue'
import SkeletonLoader from '@/components/SkeletonLoader'
import { mapGetters, mapActions } from 'vuex'

export default {
  name: 'PurchaseRequestList',
  components: {
    AppLayout,
    SkeletonLoader
  },
  data() {
    return {
      token: JSON.parse(localStorage.getItem('access_token')),
      purchaseRequests: [],
      stats: {},
      loading: false,
      filters: {
        status: '',
        categoryId: '',
        department: '',
        project: '',
        requester: '',
        dateFrom: '',
        dateTo: ''
      },
      categories: [],
      currentPage: 1,
      totalPages: 1,
      totalItems: 0,
      itemsPerPage: 9999,
      showRejectModal: false,
      rejectForm: {
        requestId: null,
        reason: ''
      },
      showAttachmentsModal: false,
      attachmentsRequest: null,
      previewAttachment: null,
      dataTable: null
    }
  },
  computed: {
    ...mapGetters(["getProjects", "getDepartments"]),

    projects() { return this.getProjects || [] },
    departments() { return this.getDepartments || [] },

    currentUserId() {
      try {
        const cached = localStorage.getItem('user_cache')
        if (cached) {
          const u = JSON.parse(cached)
          if (u && typeof u.id !== 'undefined') return Number(u.id)
        }
        const raw = localStorage.getItem('user')
        if (raw) {
          const u = JSON.parse(raw)
          if (u && typeof u.id !== 'undefined') return Number(u.id)
        }
      } catch (e) { /* ignore */ }
      return null
    },

    hasPurchaseAdd() {
      try {
        const cached = localStorage.getItem('user_cache')
        if (cached) {
          const u = JSON.parse(cached)
          return u?.Permission?.purchaseAdd === true
        }
        const perm = localStorage.getItem('permission')
        if (perm) {
          const p = JSON.parse(perm)
          return p?.purchaseAdd === true
        }
      } catch (e) { /* ignore */ }
      return false
    },

    hasPurchaseView() {
      try {
        const cached = localStorage.getItem('user_cache')
        if (cached) {
          const u = JSON.parse(cached)
          return u?.Permission?.purchaseView === true
        }
        const perm = localStorage.getItem('permission')
        if (perm) {
          const p = JSON.parse(perm)
          return p?.purchaseView === true
        }
      } catch (e) { /* ignore */ }
      return false
    },

    visiblePages() {
      const pages = []
      const maxVisible = 5
      let start = Math.max(1, this.currentPage - Math.floor(maxVisible / 2))
      let end = Math.min(this.totalPages, start + maxVisible - 1)
      
      if (end - start < maxVisible - 1) {
        start = Math.max(1, end - maxVisible + 1)
      }
      
      for (let i = start; i <= end; i++) {
        pages.push(i)
      }
      return pages
    }
  },
  mounted() {
    this.fetchDepartments()
    this.fetchProjects()
    this.loadCategories()
    this.loadStats()
    this.loadPurchaseRequests()
  },
  methods: {
    ...mapActions(["fetchProjects", "fetchDepartments"]),
    getErrorMessage(error, fallback) {
      // For fetch, error is usually just an Error with .message
      if (error && typeof error === 'object') {
        if (error.message) return error.message;
      }
      return fallback || 'An unknown error occurred';
    },

    formatCategory(request) {
      if (!request) return '-'

      // Helper to resolve by id from loaded categories
      const resolveById = (id) => {
        if (!id) return null
        const found = this.categories.find(c => Number(c.id) === Number(id))
        return found ? found.name : null
      }

      // Direct string fields (could be plain name or JSON serialized object)
      if (typeof request.category === 'string' && request.category) return request.category
      if (typeof request.tenderCategory === 'string' && request.tenderCategory) return request.tenderCategory

      // procurementCategory may be a JSON string (serialized object) or object
      let pcRaw = request.procurementCategory ?? request.procurement_category
      if (typeof pcRaw === 'string' && pcRaw.trim()) {
        // try to parse JSON string
        try {
          pcRaw = JSON.parse(pcRaw)
        } catch (e) {
          // not JSON, return the raw string
          return pcRaw
        }
      }

      // If procurementCategory is an object: check common shapes
      const pc = pcRaw
      if (pc && typeof pc === 'object') {
        if (pc.name) return pc.name
        // nested tenderCategory
        if (pc.tenderCategory && typeof pc.tenderCategory === 'object') {
          if (pc.tenderCategory.name) return pc.tenderCategory.name
          if (pc.tenderCategoryId) return resolveById(pc.tenderCategoryId)
        }
        // supplier tender category shape or legacy fields
        if (pc.tenderCategoryId) return resolveById(pc.tenderCategoryId)
        if (pc.tender_category_id) return resolveById(pc.tender_category_id)
        if (pc.categoryId) return resolveById(pc.categoryId)
        if (pc.supplierId && pc.tenderCategoryId) return resolveById(pc.tenderCategoryId)
      }

      // Fallbacks: request.category or tenderCategory objects
      if (request.category && typeof request.category === 'object' && request.category.name) return request.category.name
      if (request.tenderCategory && typeof request.tenderCategory === 'object' && request.tenderCategory.name) return request.tenderCategory.name

      return '-'
    },

    async loadStats() {
      try {
        const res = await fetch(process.env.VUE_APP_URL + '/purchase-requests/stats', {
          headers: {
            'Accept': 'application/json',
            'Authorization': this.token ? `Bearer ${this.token}` : ''
          }
        });
        if (!res.ok) {
          let msg = 'Failed to load stats';
          try {
            const err = await res.json();
            msg = err?.error || err?.message || msg;
          } catch (e) {/* ignore */}
          throw new Error(msg);
        }
        const data = await res.json();
        this.stats = data.data || data;
      } catch (error) {
        console.error('Error loading stats:', error);
        const msg = error?.message || 'Failed to load stats';
        this.$toast.error(msg);
      }
    },

    async loadPurchaseRequests() {
      this.loading = true;
      try {
        const params = {
          page: this.currentPage,
          limit: this.itemsPerPage,
          ...this.filters
        };
        Object.keys(params).forEach(key => {
          if (params[key] === '' || params[key] === null || params[key] === undefined) {
            delete params[key];
          }
        });
        const query = new URLSearchParams(params).toString();
        const res = await fetch(process.env.VUE_APP_URL + `/purchase-requests?${query}`, {
          headers: {
            'Accept': 'application/json',
            'Authorization': this.token ? `Bearer ${this.token}` : ''
          }
        });

        console.log('Fetch response for purchase requests:', res);
        
        if (!res.ok) {
          let msg = 'Failed to load purchase requests';
          try {
            const err = await res.json();
            msg = err?.error || err?.message || msg;
          } catch (e) {/* ignore */}
          throw new Error(msg);
        }
        const data = await res.json();
        // Support both {status, data: {data, totalPages, ...}} and flat {data, totalPages, ...}
        const prData = data.data?.data ? data.data : data;
        this.purchaseRequests = prData.data || [];
        this.totalPages = prData.totalPages || 1;
        this.totalItems = prData.total || prData.totalItems || 0;
      } catch (error) {
        console.error('Error loading purchase requests:', error);
        const msg = error?.message || 'Failed to load purchase requests';
        this.$toast.error(msg);
      } finally {
        this.loading = false;
        // initialize datatable after DOM updated
        setTimeout(() => {
          if (this.dataTable) {
            try { this.dataTable.destroy() } catch (e) { console.warn('Error destroying datatable', e) }
            this.dataTable = null
          }
          if (this.initializeDataTable) this.initializeDataTable()
        }, 50)
      }
    },

    async loadCategories() {
      try {
        const token = JSON.parse(localStorage.getItem('access_token'));
        const res = await fetch(process.env.VUE_APP_URL + '/tender-category', {
          method: 'GET',
          headers: { 'Content-Type': 'application/json', 'Authorization': 'Bearer ' + token }
        });
        const result = await res.json();
        this.categories = Array.isArray(result.data) ? result.data : result;
      } catch (e) {
        console.error('Failed to load categories', e);
      }
    },
    
    changePage(page) {
      if (page >= 1 && page <= this.totalPages) {
        this.currentPage = page
        this.loadPurchaseRequests()
      }
    },
    
    getStatusClass(status) {
      const s = (status || '').toString().toUpperCase()
      const classes = {
        PENDING: 'btn btn-sm btn-warning',
        ACTIVE: 'btn btn-sm btn-warning',
        PROCESSING: 'btn btn-sm btn-info',
        APPROVED: 'btn btn-sm btn-success',
        PROCESSED: 'btn btn-sm btn-success',
        COMPLETED: 'btn btn-sm btn-success',
        REJECTED: 'btn btn-sm btn-danger',
        CANCELLED: 'btn btn-sm btn-danger'
      }
      return classes[s] || 'btn btn-sm btn-warning'
    },
    
    getPriorityClass(priority) {
      const p = (priority || '').toString().toUpperCase()
      const classes = {
        LOW: 'bg-secondary',
        MEDIUM: 'bg-primary',
        HIGH: 'bg-warning text-dark',
        URGENT: 'bg-danger',
        NORMAL: 'bg-secondary'
      }
      return classes[p] || 'bg-secondary'
    },

    formatStatusLabel(value) {
      if (!value) return ''
      return value.toString().toLowerCase().split(/[_\s]+/).map(s => s.charAt(0).toUpperCase() + s.slice(1)).join(' ')
    },
    
    canEdit(request) {
      if (!request) return false
      const status = (request.status || '').toString().toUpperCase()
      if (status !== 'PENDING') return false

      const requesterId = request.requestedBy?.id ?? request.requestedById ?? null
      const me = this.currentUserId
      if (!me || !requesterId) return false

      // Only the user who raised the request can edit it
      return Number(requesterId) === Number(me)
    },
    
    canApprove() {
      // No permission check for now
      return true;
    },
    
    canReject() {
      // No permission check for now
      return true;
    },
    
    async approveRequest(requestId) {
      try {
        const res = await fetch(process.env.VUE_APP_URL + `/purchase-requests/${requestId}/approve`, {
          method: 'POST',
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json',
            'Authorization': this.token ? `Bearer ${this.token}` : ''
          },
          body: JSON.stringify({})
        });
        if (!res.ok) {
          let msg = 'Failed to approve purchase request';
          try {
            const err = await res.json();
            msg = err?.error || err?.message || msg;
          } catch (e) {/* ignore */}
          throw new Error(msg);
        }
        this.$toast.success('Purchase request approved successfully');
        this.loadPurchaseRequests();
        this.loadStats();
      } catch (error) {
        console.error('Error approving request:', error);
        const msg = error?.message || 'Failed to approve purchase request';
        this.$toast.error(msg);
      }
    },
    
    openRejectModal(request) {
      this.rejectForm.requestId = request.id
      this.rejectForm.reason = ''
      this.showRejectModal = true
    },
    
    closeRejectModal() {
      this.showRejectModal = false
      this.rejectForm.requestId = null
      this.rejectForm.reason = ''
    },
    
    async confirmRejectRequest() {
      try {
        const res = await fetch(process.env.VUE_APP_URL + `/purchase-requests/${this.rejectForm.requestId}/reject`, {
          method: 'POST',
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json',
            'Authorization': this.token ? `Bearer ${this.token}` : ''
          },
          body: JSON.stringify({ reason: this.rejectForm.reason })
        });
        if (!res.ok) {
          let msg = 'Failed to reject purchase request';
          try {
            const err = await res.json();
            msg = err?.error || err?.message || msg;
          } catch (e) {/* ignore */}
          throw new Error(msg);
        }
        this.$toast.success('Purchase request rejected');
        this.closeRejectModal();
        this.loadPurchaseRequests();
        this.loadStats();
      } catch (error) {
        console.error('Error rejecting request:', error);
        const msg = error?.message || 'Failed to reject purchase request';
        this.$toast.error(msg);
      }
    },

    openAttachmentsModal(request) {
      this.attachmentsRequest = request
      this.showAttachmentsModal = true
    },

    closeAttachmentsModal() {
      this.showAttachmentsModal = false
      this.attachmentsRequest = null
      this.previewAttachment = null
    },

    getAttachmentUrl(attachment) {
      if (!attachment || !attachment.filePath) return ''
      // attachment.filePath is "purchase-requests/filename.ext"
      return `${process.env.VUE_APP_URL}/files/${attachment.filePath}`
    },

    getFileName(attachment) {
      if (!attachment) return ''
      return attachment.originalName || attachment.filePath.split('/').pop()
    },

    openAttachmentPreview(attachment) {
      this.previewAttachment = attachment
    },

    closePreview() {
      this.previewAttachment = null
    },

    isPreviewableImage(att) {
      const name = this.getFileName(att).toLowerCase()
      return /\.(jpg|jpeg|png|gif|webp|bmp|svg)$/i.test(name)
    },

    isPreviewablePdf(att) {
      const name = this.getFileName(att).toLowerCase()
      return /\.pdf$/i.test(name)
    },

    downloadAttachment(attachment) {
      const url = this.getAttachmentUrl(attachment)
      if (!url) return
      
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', this.getFileName(attachment))
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    },

    initializeDataTable() {
      try {
        const table = this.$el.querySelector('table')
        if (!table) return
        if (this.dataTable) { this.dataTable.destroy(); this.dataTable = null }
        this.dataTable = $(table).DataTable({ dom: 'Bfrti', paging: false, responsive: true, buttons: ['colvis','pageLength', { extend: 'csv', text: 'Export CSV', className: 'btn btn-primary btn-sm' }, { extend: 'excel', text: 'Export Excel', className: 'btn btn-primary btn-sm' }, { extend: 'print', text: 'Print', className: 'btn btn-secondary btn-sm' }] })
      } catch (err) {
        console.warn('DataTable init failed', err)
      }
    }
  },
  beforeUnmount() {
    try { if (this.dataTable) { this.dataTable.destroy(); this.dataTable = null } } catch (e) { /* ignore */ }
  }
}
</script>

<style scoped>
.warehouse-inventory-card-wrapper {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 20px;
  margin: 20px 0;
}

.warehouse-inventory-card {
  background: white;
  border-radius: 8px;
  padding: 20px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  text-align: center;
}

.warehouse-inventory-card p {
  margin: 0 0 10px 0;
  color: #666;
  font-size: 13px;
}

.warehouse-inventory-card strong {
  font-size: 24px;
  font-weight: bold;
}

.warehouse-inventory-card strong.red { color: #dc3545; }
.warehouse-inventory-card strong.green { color: #28a745; }
.warehouse-inventory-card strong.orange { color: #fd7e14; }
.warehouse-inventory-card strong.blue { color: #007bff; }

  .data-table-wrapper {
  background: transparent;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 4px rgba(0,0,0,0.05);
  padding: 16px;
 }

.table {
  margin: 0;
}

.table-equal-columns {
  table-layout: fixed;
  width: 100%;
}
.table-equal-columns th,
.table-equal-columns td {
  width: 8.33%;
  overflow: hidden;
  text-overflow: ellipsis;
}
.table-equal-columns th:last-child,
.table-equal-columns td:last-child {
  overflow: visible;
  text-overflow: unset;
}

.btn-group {
  display: flex;
  gap: 5px;
}

.btn-group .btn {
  padding: 4px 8px;
  font-size: 11px;
}

.badge {
  text-transform: capitalize !important;
  font-size: 11px !important;
  padding: 4px 8px;
}

.modal-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0,0,0,0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.modal-dialog {
  background: white;
  border-radius: 8px;
  width: 90%;
  max-width: 500px;
  max-height: 90vh;
  overflow-y: auto;
}

.modal-header {
  padding: 15px 20px;
  border-bottom: 1px solid #dee2e6;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.modal-body {
  padding: 20px;
}

.modal-footer {
  padding: 15px 20px;
  border-top: 1px solid #dee2e6;
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}

.btn-close {
  background: none;
  border: none;
  font-size: 18px;
  cursor: pointer;
}

/* Preview Styles */
.preview-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1200;
  padding: 20px;
}

.preview-box {
  background: #fff;
  border-radius: 8px;
  width: 95vw;
  max-width: 1100px;
  height: 85vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.5);
}

.preview-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 11px 20px;
  background: #f8f9fa;
  border-bottom: 1px solid #dee2e6;
}

.preview-title {
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  font-size: 12px;
}

.btn-close-preview {
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: #666;
  line-height: 1;
}

.preview-body {
  flex: 1;
  overflow: auto;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #525659;
}

.preview-content {
  max-width: 100%;
  max-height: 100%;
}

iframe.preview-content {
  width: 100%;
  height: 100%;
  border: none;
}

.preview-fallback {
  padding: 40px;
  text-align: center;
  color: #fff;
}

@keyframes blink {
  0%, 50% { opacity: 1; }
  51%, 100% { opacity: 0.5; }
}

.blink {
  animation: blink 1s infinite;
}
</style>